import { ApiError, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { SliceStatus } from '@frontend/common';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toNumber } from 'lodash';

import { BadgeClient } from '../api/badge-client';
import { Badge, BadgeListResponse } from '../badge';

interface BadgeState {
    unordered: Badge[];
    badges: BadgeListResponse | null;
    accountBadges: { [accountId: string]: BadgeListResponse } | null;
    status: SliceStatus;
}

const initialState: BadgeState = {
    unordered: [],
    badges: null,
    accountBadges: null,
    status: SliceStatus.INIT
};

export const badgeSlice = createSlice({
    name: 'badges',
    initialState,
    reducers: {
        seedBadges(state, action: PayloadAction<Badge[]>) {
            state.unordered = [...state.unordered.filter((badge) => action.payload.find((b) => b.id == badge.id) == undefined), ...action.payload];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBadges.pending, (state) => {
                state.status = SliceStatus.LOADING;
            })
            .addCase(fetchBadges.fulfilled, (state, action) => {
                const startPos = toNumber(action.meta.arg.size) * toNumber(action.meta.arg.index);
                if (state.badges == null) {
                    state.badges = {
                        ...action.payload,
                        results: new Array(action.payload.count)
                    };
                    state.badges.results.splice(startPos, action.payload.results.length, ...action.payload.results);
                } else {
                    if (state.badges.results.length !== action.payload.count) {
                        state.badges.count = action.payload.count;
                        state.badges.results = new Array(action.payload.count);
                    }
                    state.badges.results.splice(startPos, action.payload.results.length, ...action.payload.results);
                }
                state.unordered = [
                    ...state.unordered.filter((b) => action.payload.results.find((res) => res.id == b.id) == undefined),
                    ...action.payload.results
                ];
            })
            .addCase(fetchAccountBadges.pending, (state) => {
                state.status = SliceStatus.LOADING;
            })
            .addCase(fetchAccountBadges.fulfilled, (state, action) => {
                state.status = SliceStatus.IDLE;
                const startPos = toNumber(action.meta.arg.queryParams?.size) * toNumber(action.meta.arg.queryParams?.index);
                if (state.accountBadges == null) {
                    state.accountBadges = { [action.meta.arg.accountId]: { ...action.payload, results: new Array(action.payload.count) } };
                    state.accountBadges[action.meta.arg.accountId].results.splice(startPos, action.payload.results.length, ...action.payload.results);
                } else if (state.accountBadges[action.meta.arg.accountId] == undefined) {
                    state.accountBadges[action.meta.arg.accountId] = { ...action.payload, results: new Array(action.payload.count) };
                    state.accountBadges[action.meta.arg.accountId].results.splice(startPos, action.payload.results.length, ...action.payload.results);
                } else {
                    if (state.accountBadges[action.meta.arg.accountId].results.length !== action.payload.count) {
                        state.accountBadges[action.meta.arg.accountId].count = action.payload.count;
                        state.accountBadges[action.meta.arg.accountId].results = new Array(action.payload.count);
                    }
                    state.accountBadges[action.meta.arg.accountId].results.splice(startPos, action.payload.results.length, ...action.payload.results);
                }
                state.unordered = [
                    ...state.unordered.filter((a) => action.payload.results.find((res) => res.id == a.id) == undefined),
                    ...action.payload.results
                ];
            });
    }
});

export const fetchBadges = createAsyncThunk<BadgeListResponse, ApiQueryParams<DefaultQueryParams>>(
    'fetchBadges',
    async (queryParams: ApiQueryParams<DefaultQueryParams>, { rejectWithValue }) => {
        try {
            return await BadgeClient.fetchBadges(queryParams);
        } catch (e) {
            if ((e as ApiError).json) return rejectWithValue(e);
            throw e;
        }
    }
);

export const fetchAccountBadge = createAsyncThunk<Badge, { accountId: string; badgeId: string }>(
    'fetchAccountBadge',
    async (variables: { accountId: string; badgeId: string }, { rejectWithValue }) => {
        try {
            return await BadgeClient.fetchAccountBadge(variables.accountId, variables.badgeId);
        } catch (e) {
            if ((e as ApiError).json) return rejectWithValue(e);
            throw e;
        }
    }
);

export const fetchAccountBadges = createAsyncThunk<BadgeListResponse, { accountId: string; queryParams?: ApiQueryParams<DefaultQueryParams> }>(
    'fetchAccountBadges',
    async (variables: { accountId: string; queryParams?: ApiQueryParams<DefaultQueryParams> }, { rejectWithValue }) => {
        try {
            return await BadgeClient.fetchAccountBadges(variables.accountId, variables.queryParams);
        } catch (e) {
            if ((e as ApiError).json) return rejectWithValue(e);
            throw e;
        }
    }
);

export const deleteAccountBadge = createAsyncThunk<void, { accountId: string; badgeId: string }>(
    'deleteAccountBadge',
    async (variables: { accountId: string; badgeId: string }, { rejectWithValue }) => {
        try {
            return await BadgeClient.removeAccountBadge(variables.accountId, variables.badgeId);
        } catch (e) {
            if ((e as ApiError).json) return rejectWithValue(e);
            throw e;
        }
    }
);

export const { seedBadges } = badgeSlice.actions;
